import {
  useEffect, useRef,
} from 'react'
import {
  Gift,
} from 'lucide-react'
import type {
  ReferralStatusResponse,
} from '../client'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  formatDollars,
} from './referralShared'

function pluralFriends(n: number): string {
  return `${n} friend${n === 1 ? '' : 's'}`
}

interface ReferralProgressProps {
  status: ReferralStatusResponse
  /** Which surface is rendering — drives styling + the analytics event. */
  source: 'strip' | 'spotlight' | 'page'
}

/**
 * Live referral-reward progress for the tiered programme (behind
 * `referral-tiered-program`). Shared by ReferralStrip's footer and the spotlight
 * modal so the reward + the user's standing read identically. All copy is derived
 * from the backend `program` payload (hotel credit per qualifying referral) —
 * change the numbers there and every surface follows.
 *
 * Fires `REFERRAL_PROGRESS_SHOWN` once per mount with the user's counts — a
 * `useRef` guard stops a React Query refetch (e.g. on window focus) that returns
 * changed counts from re-firing the impression and inflating the activation
 * metric — so we can measure in-app activation lift against the email-only baseline.
 */
export function ReferralProgress({ status, source }: ReferralProgressProps) {
  const {
    rewarded_referrals: rewarded,
    paying_pending_referrals: payingPending,
    signed_up_pending_referrals: signedUpPending,
    hotel_credit_earned_cents: earnedCents,
    hotel_credit_remaining_cents: remainingCents,
    program,
  } = status

  // Impression fires once per mount; the ref guards against React Query refetches
  // re-running this effect (deps carry the counts) and double-counting the metric.
  const impressionFired = useRef(false)
  useEffect(() => {
    if (impressionFired.current) {
      return
    }
    impressionFired.current = true
    trackEvent(OnboardingEvents.REFERRAL_PROGRESS_SHOWN, {
      source,
      rewarded_referrals: rewarded,
      paying_pending_referrals: payingPending,
      signed_up_pending_referrals: signedUpPending,
      hotel_credit_earned_cents: earnedCents,
      hotel_credit_remaining_cents: remainingCents,
    })
  }, [source, rewarded, payingPending, signedUpPending, earnedCents, remainingCents])

  // Lead with the spendable balance — that's what the user can act on. Fall back
  // to "earned" framing only when it's all been put toward stays, then to the
  // pitch for someone who hasn't earned anything yet.
  let headline: string
  if (remainingCents > 0) {
    headline = `You've got ${formatDollars(remainingCents)} to spend toward a hotel stay.`
  }
  else if (earnedCents > 0) {
    headline = `You've put all ${formatDollars(earnedCents)} of your hotel credit toward stays.`
  }
  else {
    headline = `Earn ${formatDollars(program.hotel_credit_cents_per_referral)} toward a hotel `
      + 'stay for every friend who joins and stays.'
  }

  // Stage breakdown, in reward order: qualified → on track → just signed up.
  const detail: string[] = []
  if (rewarded > 0) {
    detail.push(`${pluralFriends(rewarded)} qualified`)
  }
  if (payingPending > 0) {
    detail.push(`${payingPending} on track in the ${program.qualification_days}-day window`)
  }
  if (signedUpPending > 0) {
    detail.push(`${signedUpPending} signed up`)
  }

  // The strip embeds as a flat footer row; every other surface (spotlight
  // modal, full referral page) gets the standalone card.
  if (source !== 'strip') {
    return (
      <div className="mt-4 rounded-xl border border-[#F3D9D9] bg-[#FCEEEE] p-3">
        <div className="flex items-start gap-2.5">
          <span className="w-7 h-7 rounded-lg bg-white border border-[#F3D9D9] text-primary-600 flex items-center justify-center shrink-0">
            <Gift className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{headline}</p>
            <p className="mt-0.5 text-xs text-gray-600">
              {detail.length > 0 ? detail.join(' · ') : program.qualification_rule}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 border-t border-[#F3D9D9] bg-[#FCEEEE]">
      <span className="w-6 h-6 rounded-lg bg-white border border-[#F3D9D9] text-primary-600 flex items-center justify-center shrink-0">
        <Gift className="w-3.5 h-3.5" />
      </span>
      <span className="text-[11px] sm:text-xs text-gray-600 leading-snug">
        <span className="font-semibold text-gray-900">{headline}</span>
        {detail.length > 0 && <>{' '}{detail.join(' · ')}.</>}
      </span>
    </div>
  )
}
